import React from "react";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { AdjustMusicVolume } from "source/app/presentation/Header/Tools/Sound/Items/AdjustMusicVolume";
import { NextSongIcon } from "source/app/presentation/Header/Tools/Sound/Items/NextSongIcon";
import { ToggleMusic } from "source/app/presentation/Header/Tools/Sound/Items/ToggleMusic";
import { CurrentSongInfo } from "source/app/presentation/Header/Tools/Sound/Music/CurrentSongInfo/CurrentSongInfo";
import { FadeInArray } from "source/app/presentation/Shared/Animation/FadeInArray";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import Music from "source/app/services/Sound/Music/Music";
import { ESharedComponent } from "source/app/services/UI/State/ESharedComponent";
import { State } from "source/app/services/UI/State/State";
import styled from "styled-components";
import { IMusicMenuState } from "./IMusicMenuState";
import { LoadingProgress } from "../Shared/LoadingProgress";

export default class MusicMenu extends React.Component<{}, IMusicMenuState> {
    public static transitionTimeoutId?: number = undefined;

    private dropdownRef: React.RefObject<HTMLDivElement>;

    constructor(props: {}) {
        super(props);

        this.state = {
            showMenu: false,
            previousMusicSoundFile: undefined,
            currentMusicSoundFile: undefined,
            currentFileInfo: undefined,
            cachedB2FileInfos: undefined,
            allFileInfos: [],
            currentVolume: 1.0,
            selectedPlaylists: undefined,
            loadingProgressPercent: 0,
        };

        this.dropdownRef = React.createRef();
    }

    async componentDidMount() {
        State.ShareClass(ESharedComponent.MusicMenu, this);
        this.HandleMenuToggling();
    }

    async componentDidUpdate(
        prevProps: Readonly<{}>,
        prevState: Readonly<IMusicMenuState>,
        snapshot?: any
    ) {
        if (prevState.currentFileInfo !== this.state.currentFileInfo) {
            if (this.state.currentFileInfo) {
                const currentMusicSoundFile = Music.GetMusicSoundFile(
                    this.state.currentFileInfo
                );

                this.setState({
                    currentMusicSoundFile,
                    loadingProgressPercent: 0,
                });

                currentMusicSoundFile.Play({
                    onPlay: () => {
                        this.ExecuteCrossFadeWithPreviousSoundFile();
                        this.SetupFadeOutForCurrentSoundFile();
                        this.setState({ loadingProgressPercent: undefined });
                    },

                    onEnd: () => {
                        this.state.previousMusicSoundFile?.Unload();
                        this.setState({ previousMusicSoundFile: undefined });
                    },
                    onDownloadProgressUpdate: (progressPercent) => {
                        // console.log(`[Music]: ${progressPercent}% downloaded`);

                        this.setState({
                            loadingProgressPercent: progressPercent,
                        });
                    },
                });

                console.log(
                    `[Music] Started '${this.state.currentFileInfo.fileNameWithoutExtension}'`
                );
            } else {
                this.setState({ currentMusicSoundFile: undefined });

                console.log(
                    `[Music] Stopped '${
                        prevState.currentFileInfo!.fileNameWithoutExtension
                    }'`
                );
            }
        }

        State.ShareClass(ESharedComponent.MusicMenu, this);
    }

    public static ClearTransitionTimeout() {
        if (MusicMenu.transitionTimeoutId) {
            window.clearTimeout(MusicMenu.transitionTimeoutId);

            console.log(
                `[Music] Cleared transition timeout with id ${MusicMenu.transitionTimeoutId}`
            );

            MusicMenu.transitionTimeoutId = undefined;
        }
    }

    private SetupFadeOutForCurrentSoundFile() {
        if (!this.state.currentMusicSoundFile) {
            return;
        }

        const duration = this.state.currentMusicSoundFile.Duration();

        const fadeOutStartTime = duration! * 1000 - 14000;

        MusicMenu.transitionTimeoutId = window.setTimeout(() => {
            MusicMenu.ClearTransitionTimeout();

            this.setState({
                previousMusicSoundFile: this.state.currentMusicSoundFile,
                currentFileInfo: undefined,
            });

            Music.StartNextSong();
        }, fadeOutStartTime);
        console.log(
            `[Music] Set transition timeout with id ${MusicMenu.transitionTimeoutId}`
        );
    }

    private ExecuteCrossFadeWithPreviousSoundFile() {
        const { previousMusicSoundFile, currentMusicSoundFile } = this.state;

        if (previousMusicSoundFile && currentMusicSoundFile) {
            console.log(
                `[Music] Started fading out song`,
                previousMusicSoundFile
            );

            const timeRemainingInCurrentSong =
                previousMusicSoundFile.Duration()! -
                +previousMusicSoundFile.Seek()!;

            const crossFadeTime = Math.round(timeRemainingInCurrentSong * 1000);

            console.log(
                `[Music] previous song has ${crossFadeTime}ms remaining`
            );

            previousMusicSoundFile.FadeOut(0, crossFadeTime, () =>
                console.log(
                    `[Music] Finished fading out song over ${crossFadeTime}ms`
                )
            );

            console.log(
                `[Music] Started fading in song`,
                currentMusicSoundFile
            );

            currentMusicSoundFile.FadeIn(0, 1, crossFadeTime, () =>
                console.log(
                    `[Music] Finished fading in song over ${crossFadeTime}ms`
                )
            );
        }
    }

    private HandleMenuToggling() {
        const handleClickOutside = (event: any) => {
            if (
                this.dropdownRef !== null &&
                this.dropdownRef.current !== null &&
                !this.dropdownRef.current.contains(event.target)
            ) {
                this.setState({ ...this.state, showMenu: false });
            }
        };

        // Attach the listener to the document
        document.addEventListener("mousedown", handleClickOutside);

        // Cleanup the listener when the component unmounts
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }

    render() {
        if (IsMobileDevice()) {
            return null;
        }

        const isMusicPlaying =
            this.state.currentMusicSoundFile?.IsPlaying() ?? false;

        const subMenuItems = [
            <SubMenuItemWithBorder>
                <ToggleMusic isMusicPlaying={isMusicPlaying} />
            </SubMenuItemWithBorder>,
            <SubMenuItem>
                <CurrentSongInfo musicMenu={this} />
            </SubMenuItem>,
            <SubMenuItemWithBorder>
                <NextSongIcon />
            </SubMenuItemWithBorder>,
            <SubMenuItemWithBorder>
                <AdjustMusicVolume volume={this.state.currentVolume} />
            </SubMenuItemWithBorder>,
        ];

        const isLoading = this.state.loadingProgressPercent !== undefined;

        return (
            <Container ref={this.dropdownRef}>
                <Hyperlink
                    whenClicked={() =>
                        this.setState({
                            ...this.state,
                            showMenu: !this.state.showMenu,
                        })
                    }
                    classes={StyleClassAuto({
                        color: isLoading ? `orange` : `rgb(208, 208, 208)`,
                        opacity: isMusicPlaying || isLoading ? 1.0 : 0.5,
                        transition: `all 0.5s ease !important`,
                    })}
                >
                    <Glyphicon icon={EGlyphicon.music} />
                    <LoadingProgress
                        loadingProgressPercent={
                            this.state.loadingProgressPercent
                        }
                    />
                </Hyperlink>
                <div
                    className={GetDropDownContentStyleClass(
                        this.state.showMenu
                    )}
                >
                    <FadeInArray
                        label=""
                        render={(item) => item}
                        array={subMenuItems}
                        itemDelayMs={50}
                    />
                </div>
            </Container>
        );
    }
}

const Container = styled.div`
    position: relative;
    display: inline-block;
    margin: 0px 5px;
`;

const GetDropDownContentStyleClass = (show: boolean) =>
    StyleClassAuto({
        display: show ? "flex" : "none",
        flexDirection: "column",
        position: "absolute",
        minWidth: "200px",
        boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.2)",
        padding: "12px 16px",
        zIndex: 1,
        right: 0,
        background: "#2e2e2e57",
        borderRadius: "4px",
    });

const SubMenuItem = styled.div`
    padding: 5px;
`;

const SubMenuItemWithBorder = styled(SubMenuItem)`
    border-radius: 3px;
    border: 1px dotted #c0c0c017;
    &:hover {
        border: 1px solid #c0c0c070;
    }
`;
